import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WordReveal } from '@/components/ui/word-reveal';

export function Faq() {
  const items = [
    {
      q: 'How long does a typical filing take?',
      a: 'It depends on the service — GST registrations usually close in 3–7 working days, company incorporation 7–15 days. Your Associate shares a timeline upfront in chat and keeps you posted as each stage completes.',
    },
    {
      q: 'Do you store my documents securely?',
      a: 'Yes. Uploads go straight to a private encrypted bucket via signed URLs — we never hold them on our servers. Access is limited to you, your assigned Associate, and our admin team.',
    },
    {
      q: 'What if I need a refund?',
      a: 'Every service has a stage-based refund policy printed on the invoice. If work has not started you get a full refund (minus the gateway fee); partial refunds are prorated based on completion stage.',
    },
    {
      q: 'Can I register as both a client and an associate?',
      a: 'Each email belongs to either a Client or an Associate — not both. Use a different email for your Associate account (Associate login) if you also want to offer services on the platform.',
    },
  ];

  return (
    <section
      id="faq"
      className="scroll-mt-20 bg-white py-20 md:py-28 border-t border-gray-100 relative"
    >
      <div className="mx-auto w-full max-w-7xl 2xl:max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Left Title Column */}
          <div className="lg:col-span-5 space-y-4">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#15803d]">
              FAQ
            </span>
            <WordReveal
              as="h2"
              text="Frequently Asked Questions"
              className="font-display text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl leading-tight"
              stagger={0.06}
            />
            <p className="text-sm text-gray-600 leading-relaxed font-normal">
              Still have a question? Reach out to us at{' '}
              <a
                href="mailto:support@ecregi.com"
                className="text-[#15803d] underline font-semibold hover:text-[#166534]"
              >
                support@ecregi.com
              </a>
            </p>
          </div>

          {/* Right Accordion List Column */}
          <div className="lg:col-span-7 space-y-4">
            {items.map((it, i) => (
              <FaqItem key={i} q={it.q} a={it.a} defaultOpen={i === 0} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqItem({
  q,
  a,
  defaultOpen = false,
  index,
}: {
  q: string;
  a: string;
  defaultOpen?: boolean;
  index: number;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="rounded-2xl border border-gray-200/90 bg-white shadow-xs overflow-hidden transition-all duration-300 hover:border-emerald-300/80"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left hover:bg-emerald-50/30 transition-colors cursor-pointer"
      >
        <h3 className="font-display text-base font-bold text-gray-900 leading-snug">{q}</h3>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-transform duration-300 ${
            open ? 'rotate-45 bg-[#e8f8f0] text-[#15803d]' : 'bg-gray-100 text-gray-500'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M12 6v12m6-6H6"
            />
          </svg>
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-sm leading-relaxed text-gray-600 border-t border-gray-100 pt-4 font-normal">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
